import { Link, useRouterState } from "@tanstack/react-router";
import {
  LayoutDashboard, Radar, MessageSquare, BarChart3, Calendar, Settings,
  ChevronLeft, ChevronRight, Building2, Users, FolderOpen, ScrollText, CreditCard, Palette, FileText,
  Search, Sparkles, LogOut, Radio,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useMe } from "@/hooks/use-me";
import { useBranding } from "@/hooks/use-branding";

type NavItem = {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  badge?: number;
};

const NAV: NavItem[] = [
  { to: "/dashboard", label: "Inicio", icon: LayoutDashboard },
  { to: "/monitor", label: "Monitorización", icon: Radar },
  { to: "/responder", label: "Responder", icon: MessageSquare, badge: 23 },
  { to: "/analyze", label: "Analizar", icon: BarChart3 },
  { to: "/schedule", label: "Programar", icon: Calendar },
  { to: "/clients", label: "Clientes", icon: Building2 },
  { to: "/media", label: "Medios tradicionales", icon: Radio },
];

// Para roles scoped (cliente/supervisor) solo se muestran rutas de medios.
const SCOPED_NAV: NavItem[] = [
  { to: "/media", label: "Dashboard", icon: LayoutDashboard },
  { to: "/media/campaigns", label: "Campañas", icon: Calendar },
  { to: "/media/reports", label: "Reportes", icon: FileText },
];

const ADMIN_ITEMS: NavItem[] = [
  { to: "/admin", label: "Cuentas", icon: Building2 },
  { to: "/admin/users", label: "Usuarios", icon: Users },
  { to: "/admin/projects", label: "Proyectos", icon: FolderOpen },
  { to: "/admin/outlet-kinds", label: "Tipos de medios", icon: Radio },
  { to: "/admin/branding", label: "Branding", icon: Palette },
  { to: "/admin/login-content", label: "Login content", icon: FileText },
  { to: "/admin/audit", label: "Auditoría", icon: ScrollText },
  { to: "/admin/billing", label: "Billing", icon: CreditCard },
];

export function Sidebar({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const { location } = useRouterState();
  const { data: me } = useMe();
  const branding = useBranding();
  const isSuper = me?.role === "superadmin";
  const isScoped = me?.role === "cliente" || me?.role === "supervisor";
  const nav = isSuper ? ADMIN_ITEMS : (isScoped ? SCOPED_NAV : NAV);

  function isActive(to: string) {
    if (to === "/admin") {
      return location.pathname === "/admin" || location.pathname.startsWith("/admin/accounts");
    }
    return location.pathname === to || location.pathname.startsWith(to + "/");
  }

  return (
    <aside
      className={cn(
        "bg-white dark:bg-ink-900 border-r border-ink-200 dark:border-ink-800 flex flex-col transition-all shrink-0",
        collapsed ? "w-[64px]" : "w-[232px]",
      )}
    >
      {/* Brand */}
      <div className="h-14 px-3 flex items-center gap-2 border-b border-ink-100 dark:border-ink-800">
        {branding.logo_url ? (
          <img src={branding.logo_url} alt={branding.brand_name} className="w-7 h-7 rounded-md shrink-0" />
        ) : (
          <div className="tm-logo-mark shrink-0" />
        )}
        {!collapsed && (
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-ink-900 dark:text-white text-sm leading-none truncate">
              {branding.brand_name}
            </div>
            <div className="text-[10px] text-ink-500 mt-0.5">{isSuper ? "Panel admin" : "Workspace"}</div>
          </div>
        )}
        <button
          onClick={onToggle}
          className="w-7 h-7 rounded-md hover:bg-ink-100 dark:hover:bg-ink-800 text-ink-500 flex items-center justify-center shrink-0"
          aria-label={collapsed ? "Expandir sidebar" : "Colapsar sidebar"}
          title={collapsed ? "Expandir" : "Colapsar"}
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      </div>

      {/* Search */}
      {!collapsed && (
        <div className="px-3 pt-3">
          <button
            type="button"
            className="w-full h-9 rounded-lg bg-ink-50 dark:bg-ink-800 hover:bg-ink-100 dark:hover:bg-ink-700 flex items-center gap-2 px-3 text-xs text-ink-500"
          >
            <Search size={13} />
            Buscar
            <span className="ml-auto text-[10px] font-mono px-1.5 py-0.5 rounded bg-white dark:bg-ink-900 ring-soft">
              ⌘K
            </span>
          </button>
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        {!collapsed && (
          <p className="px-2 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-widest text-ink-400">
            {isSuper ? "Plataforma" : "Workspace"}
          </p>
        )}
        {nav.map((it) => {
          const Icon = it.icon;
          const active = isActive(it.to);
          return (
            <Link
              key={it.to}
              to={it.to}
              className={cn(
                "w-full flex items-center gap-3 px-2.5 h-9 rounded-lg text-sm transition-colors",
                active
                  ? "bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300"
                  : "text-ink-700 dark:text-ink-200 hover:bg-ink-50 dark:hover:bg-ink-800",
              )}
              title={collapsed ? it.label : undefined}
            >
              <Icon size={16} className="shrink-0" />
              {!collapsed && <span className="flex-1 text-left truncate">{it.label}</span>}
              {!collapsed && it.badge != null && (
                <span className="px-1.5 h-5 rounded-full bg-brand-50 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 text-[10px] font-semibold flex items-center">
                  {it.badge}
                </span>
              )}
            </Link>
          );
        })}

        {!isSuper && (
          <>
            {!collapsed && <div className="h-px bg-ink-100 dark:bg-ink-800 my-3" />}
            {!collapsed && (
              <p className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-widest text-ink-400">Ajustes</p>
            )}
            <Link
              to="/settings"
              className={cn(
                "w-full flex items-center gap-3 px-2.5 h-9 rounded-lg text-sm transition-colors",
                isActive("/settings")
                  ? "bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300"
                  : "text-ink-700 dark:text-ink-200 hover:bg-ink-50 dark:hover:bg-ink-800",
              )}
              title={collapsed ? "Configuración" : undefined}
            >
              <Settings size={16} className="shrink-0" />
              {!collapsed && <span className="flex-1 text-left">Configuración</span>}
            </Link>
          </>
        )}
      </nav>

      {/* Bottom upgrade card (only non-super, not collapsed) */}
      {!isSuper && !collapsed && (
        <div className="p-3">
          <div className="rounded-xl bg-gradient-to-br from-brand-600 to-brand-800 text-white p-4 relative overflow-hidden">
            <div className="absolute -right-4 -bottom-4 w-20 h-20 rounded-full bg-warn/30 blur-xl" />
            <div className="text-xs font-semibold flex items-center gap-1.5">
              <Sparkles size={12} /> AI Insights
            </div>
            <p className="text-[11px] text-brand-100/90 mt-1 leading-relaxed">
              Detección automática de crisis con IA · disponible en tu plan.
            </p>
            <button className="mt-3 h-7 w-full rounded-md bg-white/15 hover:bg-white/25 text-xs font-medium transition-colors">
              Activar
            </button>
          </div>
        </div>
      )}

      {/* Collapsed footer logout (admin only — accent navigation) */}
      {isSuper && collapsed && (
        <div className="p-2 border-t border-ink-100 dark:border-ink-800 flex items-center justify-center">
          <LogOut size={14} className="text-ink-400" />
        </div>
      )}
    </aside>
  );
}
